import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  mailAssignmentMutationSchema,
  mailAssignmentStateSchema,
  mailEmailFieldBindingInputSchema,
  mailEmailFieldBindingsSchema,
  mailRecordListSchema,
  mailRecordQuerySchema,
  mailRematchPreviewSchema,
  partnerListResponseSchema,
  customerContactSchema,
  type MailAssignmentMutation,
  type MailEmailFieldBindingInput,
  type MailRecordQuery,
} from '@erp/shared';
import { apiFetch } from '@/lib/api-client';
import { useMailScope } from './use-mail-accounts';

export function useMailAssignments(sourceId: string) {
  const scope = useMailScope();
  return useQuery({
    queryKey: [...scope.key, 'assignments', sourceId],
    enabled: scope.enabled && Boolean(sourceId),
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () =>
      apiFetch(
        `/api/v1/mail/sources/${encodeURIComponent(sourceId)}/assignments`,
        mailAssignmentStateSchema,
      ),
  });
}
export function useUpdateMailAssignments() {
  const scope = useMailScope(),
    client = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, input }: { sourceId: string; input: MailAssignmentMutation }) =>
      apiFetch(
        `/api/v1/mail/sources/${encodeURIComponent(sourceId)}/assignments`,
        mailAssignmentStateSchema,
        { method: 'PUT', body: mailAssignmentMutationSchema.parse(input) },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: scope.key }),
  });
}
export function useRecordMail(customerId: string, query: MailRecordQuery) {
  const scope = useMailScope();
  return useQuery({
    queryKey: [...scope.key, 'record', customerId, query],
    enabled: scope.enabled && Boolean(customerId),
    staleTime: 0,
    queryFn: () =>
      apiFetch(
        `/api/v1/mail/records/${encodeURIComponent(customerId)}?${new URLSearchParams(
          Object.entries(mailRecordQuerySchema.parse(query))
            .filter((item): item is [string, string | number] => item[1] !== undefined)
            .map(([key, value]) => [key, String(value)]),
        )}`,
        mailRecordListSchema,
      ),
  });
}
export function useMailEmailFields(enabled: boolean) {
  const scope = useMailScope();
  return useQuery({
    queryKey: [...scope.key, 'email-fields'],
    enabled: scope.enabled && enabled,
    staleTime: 0,
    queryFn: () => apiFetch('/api/v1/mail/email-fields', mailEmailFieldBindingsSchema),
  });
}
export function useUpdateMailEmailField() {
  const scope = useMailScope(),
    client = useQueryClient();
  return useMutation({
    mutationFn: (input: MailEmailFieldBindingInput) =>
      apiFetch('/api/v1/mail/email-fields', mailEmailFieldBindingsSchema, {
        method: 'PUT',
        body: mailEmailFieldBindingInputSchema.parse(input),
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: scope.key }),
  });
}
export function usePreviewMailRematch() {
  return useMutation({
    mutationFn: (sourceIds: string[]) =>
      apiFetch('/api/v1/mail/rematch-previews', mailRematchPreviewSchema, {
        method: 'POST',
        body: { sourceIds },
      }),
    gcTime: 0,
  });
}
export function useApplyMailRematch() {
  const scope = useMailScope(),
    client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, snapshotHash, key }: { id: string; snapshotHash: string; key: string }) =>
      apiFetch(
        `/api/v1/mail/rematch-previews/${encodeURIComponent(id)}/apply`,
        mailRematchPreviewSchema,
        { method: 'POST', body: { snapshotHash }, headers: { 'Idempotency-Key': key } },
      ),
    gcTime: 0,
    onSuccess: () => client.invalidateQueries({ queryKey: scope.key }),
  });
}
export function useMailPartnerLookup(q: string, enabled: boolean) {
  const scope = useMailScope();
  return useQuery({
    queryKey: [...scope.key, 'partner-lookup', q],
    enabled: scope.enabled && enabled && q.trim().length >= 2,
    queryFn: () =>
      apiFetch(
        `/api/v1/partners?${new URLSearchParams({ type: 'all', page: '1', pageSize: '50', q })}`,
        partnerListResponseSchema,
      ),
  });
}
const contactsSchema = z.object({ data: z.array(customerContactSchema) });
export function useMailPartnerContacts(customerId: string) {
  const scope = useMailScope();
  return useQuery({
    queryKey: [...scope.key, 'partner-contacts', customerId],
    enabled: scope.enabled && Boolean(customerId),
    queryFn: () =>
      apiFetch(`/api/v1/customers/${encodeURIComponent(customerId)}/contacts`, contactsSchema),
  });
}
